import { useCallback, useRef, useState } from 'react'

import type { BillingUsageEntry } from '@/components/billing/billing.interface'
import { parseBillingError } from '@/utils/billing-error.util'
import { transformUsageItemList } from '@/utils/usage-transform.util'

import type { UsageHistoryParams } from './use-ledger-api.hook'
import { useLedgerApi } from './use-ledger-api.hook'

export interface UseUsageHistoryReturn {
  data: BillingUsageEntry[]
  total: number
  isLoading: boolean
  error: string | null
  fetchUsageHistory: (params?: UsageHistoryParams) => Promise<void>
  reset: () => void
}

const useUsageState = () => {
  const [data, setData] = useState<BillingUsageEntry[]>([])
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isFetchingRef = useRef(false)

  return { data, setData, total, setTotal, isLoading, setIsLoading, error, setError, isFetchingRef }
}

export const useUsageHistory = (): UseUsageHistoryReturn => {
  const { getUsageHistory } = useLedgerApi()
  const state = useUsageState()

  const fetchUsageHistory = useCallback(
    async (params: UsageHistoryParams = {}) => {
      if (state.isFetchingRef.current) return

      state.isFetchingRef.current = true
      state.setIsLoading(true)
      state.setError(null)

      try {
        const response = await getUsageHistory(params)
        state.setData(transformUsageItemList(response.data))
        state.setTotal(response.total)
      } catch (err) {
        state.setError(await parseBillingError(err))
        state.setData([])
        state.setTotal(0)
      } finally {
        state.setIsLoading(false)
        state.isFetchingRef.current = false
      }
    },
    [getUsageHistory, state.setIsLoading, state.setError, state.setData, state.setTotal]
  )

  const reset = useCallback(() => {
    state.setData([])
    state.setTotal(0)
    state.setIsLoading(false)
    state.setError(null)
  }, [state.setData, state.setTotal, state.setIsLoading, state.setError])

  return {
    data: state.data,
    total: state.total,
    isLoading: state.isLoading,
    error: state.error,
    fetchUsageHistory,
    reset,
  }
}
